import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
} from "@b2bportal/core-hotels";
import { HotelSortOptionEnum } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  useHotelsAvailabilityFilters,
  useFilterDialog as useCoreFilterDialog,
} from "@tenant/hotels";

/**
 * Package-specific filter dialog hook for hotels in the packages flow.
 * Uses package total pricing (hotel + flight + car) instead of standalone hotel prices
 * for the price filter range. Should not be used for standalone hotel searches.
 */
export const usePackageHotelFilterDialog = () => {
  const {
    context: coreContext,
    handlers: coreHandlers,
    text,
  } = useCoreFilterDialog();

  const { t } = useI18nContext();

  // Get package-aware filterOptions (with package prices)
  const { context: packageFilterContext } = useHotelsAvailabilityFilters();

  const dispatch = useDispatch();
  const selectedSort = useSelector(HotelsAvailabilitySelectors.getSort);
  const priceFilterFromRedux = useSelector(
    HotelsAvailabilitySelectors.getPriceFilter
  );

  const [sortOption, setSortOption] = useState(selectedSort);

  const filtersChanged = useMemo(
    () => coreContext.filtersChanged || sortOption !== selectedSort,
    [coreContext.filtersChanged, sortOption, selectedSort]
  );

  const updateValuesToMatchState = useCallback(() => {
    // For price range, use package-aware filterOptions instead of core's
    // When the filter is reset (0, MAX_SAFE_INTEGER), use the available package price range
    const effectivePriceRange =
      priceFilterFromRedux.min === 0 &&
      priceFilterFromRedux.max === Number.MAX_SAFE_INTEGER
        ? {
            min: packageFilterContext.filterOptions.priceRange?.min.value ?? 0,
            max:
              packageFilterContext.filterOptions.priceRange?.max.value ??
              Number.MAX_SAFE_INTEGER,
          }
        : priceFilterFromRedux;

    // Set the price range with package-aware values
    coreHandlers.onPriceChange({
      minPrice: effectivePriceRange.min,
      maxPrice: effectivePriceRange.max,
    });

    // Update all other filter values from Redux state
    coreHandlers.onAmenitiesChange(coreContext.amenities);
    coreHandlers.onCancellationChange(coreContext.cancellation);
    coreHandlers.onRatingChange(coreContext.starRating);

    if (coreHandlers.onPayLaterChange) {
      coreHandlers.onPayLaterChange(coreContext.payLater);
    }
    if (coreHandlers.onHotelNameChange) {
      coreHandlers.onHotelNameChange(coreContext.hotelName ?? "");
    }
    if (coreHandlers.onMealPlanChange) {
      coreHandlers.onMealPlanChange(coreContext.mealPlan ?? []);
    }
    if (coreHandlers.onStayTypeChange) {
      coreHandlers.onStayTypeChange(coreContext.stayType ?? []);
    }
    if (coreHandlers.onHomeAmenitiesChange) {
      coreHandlers.onHomeAmenitiesChange(coreContext.homeAmenities ?? []);
    }
    if (coreHandlers.onHomeBedsAndRoomsChange) {
      coreHandlers.onHomeBedsAndRoomsChange(coreContext.homeRoomsAndBeds);
    }
    if (coreHandlers.onUserRatingChange) {
      coreHandlers.onUserRatingChange(coreContext.userRating ?? 0);
    }

    setSortOption(selectedSort);
  }, [
    coreHandlers,
    coreContext,
    packageFilterContext.filterOptions.priceRange,
    priceFilterFromRedux,
    selectedSort,
  ]);

  const resetAllFilters = useCallback(() => {
    // Reset all filters in Redux
    coreHandlers.resetAllFilters();

    // Reset sort to recommended
    dispatch(
      HotelsAvailabilityActions.setSort(HotelSortOptionEnum.Recommended)
    );
  }, [dispatch, coreHandlers]);

  const applyFilter = useCallback(() => {
    coreHandlers.applyFilter();
    dispatch(HotelsAvailabilityActions.setSort(sortOption));
  }, [dispatch, coreHandlers, sortOption]);

  return {
    text,
    context: {
      ...coreContext,
      // Override filterOptions with package-aware version (has package prices)
      filterOptions: packageFilterContext.filterOptions,
      totalFilterCount: packageFilterContext.totalFilterCount,
      filtersChanged,
      sortOption,
    },
    handlers: {
      ...coreHandlers,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
      onSortChange: setSortOption,
    },
  };
};
